import { useCallback, useMemo, useState } from 'react';

import {
  EventDateRequest,
  useCreateEventDate,
  useShowCampaignById_new,
  useShowCampaignSettings,
  useUpdateEventDate,
} from '@givebacks/api/store';
import { AxiosError, isAxiosError, useFormListValidation } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { DateTime } from 'luxon';
import { Alert, Button, Group, Modal, ModalProps, Stack, notifications } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { DeleteEventDateModal } from '../DeleteEventDateModal';
import { AddEditEventDateFormWrapper } from './AddEditEventDateForm';
import { AddEventDateProvider, useAddEventDate } from './AddEventDate.context';
import { EventDateFormType } from './EventDateForm.types';

interface AddEditEventDateModalProps extends ModalProps {}

type AddEditEventDateFormType = {
  event_date: UseFormReturnType<EventDateFormType>;
};

interface AddEditEventDateModalFooterProps extends Partial<AddEditEventDateModalProps> {
  setError: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddEditEventDateModalFooter = ({ onClose, setError }: AddEditEventDateModalFooterProps) => {
  const { causeId, eventId, dateId, isDuplicate } = useAddEventDate();
  const { data: event } = useShowCampaignById_new({ causeId, campaignId: eventId });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { validateForms, useGetForm } = useFormListValidation<AddEditEventDateFormType>();

  const { mutate: createEventDate } = useCreateEventDate();
  const { mutate: updateEventDate } = useUpdateEventDate();

  const form = useGetForm('event_date');

  const handleCreateEventDate = useCallback(
    (eventDate: EventDateRequest) => {
      if (!event) return;

      createEventDate(
        {
          eventId: event.uuid,
          causeId: event.cause_id,
          eventDate,
        },
        {
          onSuccess() {
            notifications.show({ variant: 'success', message: 'Event date created.' });
            onClose?.();
          },
          onError(e) {
            const error = e as Error | AxiosError;
            if (
              isAxiosError(error) &&
              error?.response?.data.error.includes('unique') &&
              error.response?.data.attributes.includes('title')
            ) {
              setError(true);
              form.setErrors({ title: true });
            } else {
              notifications.show({ message: getErrorMessage_v2(error), variant: 'error' });
            }
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event, createEventDate, onClose, setError]
  );

  const handleUpdateEventDate = useCallback(
    (eventDate: EventDateRequest) => {
      if (!event) return;
      updateEventDate(
        {
          eventId: event.uuid,
          causeId: event.cause_id,
          eventDate,
          dateId,
        },
        {
          onSuccess() {
            notifications.show({ variant: 'success', message: 'Event date updated.' });
            onClose?.();
          },
          onError(e) {
            const error = e as Error | AxiosError;
            if (
              isAxiosError(error) &&
              error?.response?.data.error.includes('unique') &&
              error.response?.data.attributes.includes('title')
            ) {
              setError(true);
              form.setErrors({ first_name: true, last_name: true });
            } else {
              notifications.show({ message: getErrorMessage_v2(error), variant: 'error' });
            }
          },
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [event, updateEventDate, dateId, onClose, setError]
  );

  const handleSave = useCallback(() => {
    if (validateForms().isValid) {
      const eventDate = form?.values;

      const startingTime = DateTime.fromISO(eventDate?.starting_at ?? '');
      const endingTime = DateTime.fromISO(eventDate?.ending_at ?? '');

      const startingDate =
        eventDate?.date &&
        DateTime.fromJSDate(eventDate?.date).set({
          hour: startingTime?.hour,
          minute: startingTime?.minute,
        });
      const endingDate =
        eventDate?.date &&
        DateTime.fromJSDate(eventDate?.date).set({
          hour: endingTime?.hour,
          minute: endingTime?.minute,
        });

      const eventDateRequest = {
        title: eventDate?.title,
        starting_at: startingDate?.toUTC().toString(),
        ending_at: endingDate?.toUTC().toString(),
        description: eventDate?.description,
        ticket_store_item_ids: eventDate?.selectedTickets,
      };
      if (dateId && !isDuplicate) {
        handleUpdateEventDate(eventDateRequest);
      } else {
        handleCreateEventDate(eventDateRequest);
      }
    }
  }, [
    validateForms,
    dateId,
    isDuplicate,
    handleUpdateEventDate,
    handleCreateEventDate,
    form?.values,
  ]);

  const isEventFinished = useMemo(() => {
    return event?.status === 'finished';
  }, [event?.status]);

  const isSingleDateEvent = useMemo(() => {
    return event?.type === 'StoreItems::Event' && event?.single_date;
  }, [event]);

  const showDelete = useMemo(
    /* we will allow deletion of an existing event date if 
    - the event is not finished
    - the event is not a single date event
    - the event date is not a duplicate
    */
    () => dateId && !isEventFinished && !isSingleDateEvent && !isDuplicate,
    [dateId, isEventFinished, isSingleDateEvent, isDuplicate]
  );

  const handleDeleteSuccess = useCallback(() => {
    setIsDeleteModalOpen(false);
    onClose?.();
  }, [onClose, setIsDeleteModalOpen]);

  return (
    <>
      <Modal.AffixedFooter>
        <Group justify={showDelete ? 'space-between' : 'flex-end'}>
          {showDelete && (
            <Button
              size="md"
              variant="subtle"
              color="cerise"
              onClick={() => setIsDeleteModalOpen(true)}
            >
              Delete
            </Button>
          )}
          <Group justify="flex-end">
            <Button size="md" variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button miw={160} size="md" onClick={handleSave}>
              {dateId && !isDuplicate ? 'Save Changes' : 'Save'}
            </Button>
          </Group>
        </Group>
      </Modal.AffixedFooter>
      <DeleteEventDateModal
        causeId={causeId}
        eventId={eventId}
        dateId={dateId}
        opened={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onSuccess={handleDeleteSuccess}
      />
    </>
  );
};

const AddEditEventDateModal = ({ opened, onClose }: AddEditEventDateModalProps) => {
  const { causeId, eventId, dateId, isDuplicate } = useAddEventDate();
  const { data: event } = useShowCampaignById_new({ campaignId: eventId, causeId });

  const { data: campaignSettings } = useShowCampaignSettings({
    campaignId: eventId,
    causeId: causeId,
  });

  const [isUniqueError, setIsUniqueError] = useState(false);

  const modalTitle = useMemo(() => {
    if (campaignSettings.isCampaignReadOnly) {
      return 'View Event Date';
    }
    if (dateId) {
      if (isDuplicate) {
        return 'Duplicate Event Date';
      } else {
        return 'Edit Event Date';
      }
    } else {
      return 'Add Event Date';
    }
  }, [campaignSettings.isCampaignReadOnly, dateId, isDuplicate]);

  const handleModalClose = useCallback(() => {
    setIsUniqueError(false);
    onClose();
  }, [onClose]);

  const showPublishedWarning = dateId && !isDuplicate && event?.status === 'published';

  return (
    <Modal opened={opened} onClose={handleModalClose} title={modalTitle} size="lg">
      <Stack gap="lg">
        {showPublishedWarning && (
          <Alert
            type="warning"
            title="Please inform attendees if you edit event date details for dates with tickets purchased."
          />
        )}
        {
          <Alert.Collapse in={!!isUniqueError} title="Event date already exists" type="error">
            It seems you’re trying to create a new event date that already exists. Please change the
            title, choose a different date or check the existing event dates to avoid conflicts.
          </Alert.Collapse>
        }
        <AddEditEventDateFormWrapper />
        {!campaignSettings.isCampaignReadOnly && (
          <AddEditEventDateModalFooter onClose={handleModalClose} setError={setIsUniqueError} />
        )}
      </Stack>
    </Modal>
  );
};

type AddEditEventDateModalWrapperProps = AddEditEventDateModalProps & {
  dateId?: string;
  eventId?: string;
  causeId: string;
  isDuplicate?: boolean;
};

const AddEditEventDateModalWrapper = ({
  opened,
  onClose,
  dateId,
  isDuplicate,
  eventId,
  causeId,
}: AddEditEventDateModalWrapperProps) => {
  return (
    <AddEventDateProvider value={{ causeId, eventId, dateId, isDuplicate }}>
      <AddEditEventDateModal opened={opened} onClose={onClose} />
    </AddEventDateProvider>
  );
};

export { AddEditEventDateModalWrapper as AddEditEventDateModal };
